import type { ReactNode } from 'react';
import classnames from 'classnames';
import { useEffect, useState } from 'react';
import Scroll from 'react-scroll';
import type { ProcessActivityAsset } from '@common/validation/processActivityAsset';
import { useFrontendSettings } from '@frontend/utils/hooks/useFrontendSettings';
import type { ProcessScrollItem } from '../../../../common/enums/processScrollItem';
import type { IAsset } from '../../interfaces/asset';
import type { IEmployment } from '../../interfaces/employment';
import type { IOrganization } from '../../interfaces/organization';
import type { IPractitioner } from '../../interfaces/practitioner';
import { FilesUrlBase } from '../../../../common/enums/filesUrlBase';
import { checkIsImageOrPdf } from '../../../../common/utils/checkIsImageOrPdf';
import { getDisplayName } from '../../../../common/utils/getDisplayName';
import { isBrowserInternetExplorer } from '../../../../common/utils/isBrowserInternetExplorer';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import { FormDivider, FormGroup, HeaderDivider, Icon, IconColor, UserTextParagraph } from '../../elements/common';
import { ViewerModal } from '../../elements/viewerModal/ViewerModal';

/**
 * @deprecated use ksyos/design-system components instead. Remove if no longer used.
 */
export function CollapseWrapper(props: {
    children: ReactNode;
    id?: string;
    isPreviousRequest?: boolean;
    processFormId: string;
}) {
    return (
        <Scroll.Element name={props.id ? props.id : ''}>
            <div
                className={classnames('collapse-wrapper', props.isPreviousRequest ? 'is-previous' : '')}
                id={props.processFormId}
            >
                {props.children}
            </div>
        </Scroll.Element>
    );
}

/**
 * @deprecated use ksyos/design-system components instead. Remove if no longer used.
 */
export function ProcessItemScrollElement(props: { children?: ReactNode; item: ProcessScrollItem; requestId: string }) {
    return (
        <Scroll.Element name={`${props.requestId}-${props.item}`}>
            {props.children ? props.children : null}
        </Scroll.Element>
    );
}

/**
 * @deprecated use ksyos/design-system components instead. Remove if no longer used.
 */
export function ProfileImageWithName(props: {
    organization: IOrganization | null;
    practitioner: IPractitioner | null;
}) {
    const [employment, setEmployment] = useState<IEmployment>();

    useEffect(() => {
        if (props.organization?.id && props.practitioner) {
            loadEmployment();
        }
    }, []);

    let name = '';
    if (props.practitioner) {
        name += getDisplayName(props.practitioner);
        if (employment && employment.profession) {
            name += `, ${translateKey(`professionEnum.${employment.profession}`)}`;
        }
    }
    if (props.organization) {
        if (props.organization.name) {
            if (props.practitioner) {
                name += ` - ${props.organization.name}`;
            } else {
                name += ` ${props.organization.name}`;
            }
        }
        if (props.organization.city) {
            name += ` (${props.organization.city})`;
        }
    }

    return (
        <div className="layout">
            <Icon
                color={IconColor.inverted}
                icon="male-care-provider"
            />
            {name && <p>{name}</p>}
        </div>
    );

    async function loadEmployment() {
        const url = `/api/employments/${props.organization?.id}/practitioners/?isActive=true`;
        const result = await fetchJson(url, 'GET');

        const employments: IEmployment[] = result.json.items;
        if (employments.length > 0 && props.practitioner) {
            const currentEmployment = employments.find((item) => item.practitionerId === props.practitioner!.id);
            if (currentEmployment) {
                setEmployment(currentEmployment);
            }
        }
    }
}

interface ProcessContainerProps {
    children: ReactNode;
    isActiveActivity?: boolean;
    isAdministrativeActivity?: boolean;
    noBorderTop?: boolean;
}

/**
 * @deprecated use ksyos/design-system components instead. Remove if no longer used.
 */
export function ProcessContainer(props: ProcessContainerProps) {
    let className = 'form-container';
    const { frontendSettings } = useFrontendSettings();
    const { children, noBorderTop, isActiveActivity, isAdministrativeActivity } = props;

    if (isAdministrativeActivity) {
        className += ' administrative-activity';
    }
    if (isActiveActivity && frontendSettings.experimentalCarepathOphthalmologyEnabled) {
        className += ' active-activity';
    }

    if (noBorderTop) {
        className += ' no-border-top';
    }

    return <div className={className}>{children}</div>;
}

/**
 * @deprecated use ksyos/design-system components instead. Remove if no longer used.
 */
export function EditProcessContainer(props: {
    children: ReactNode;
    experimentalCarepathOphthalmologyEnabled: boolean;
    isActiveActivity?: boolean;
    isAdministrativeActivity?: boolean;
}) {
    const { experimentalCarepathOphthalmologyEnabled, isAdministrativeActivity, isActiveActivity } = props;
    let className = 'form-container form-container--paddingmod no-margin editable';
    if (isAdministrativeActivity) {
        className += ' administrative-activity';
    }
    if (isActiveActivity && experimentalCarepathOphthalmologyEnabled) {
        className += ' active-activity';
    }

    return <div className={className}>{props.children}</div>;
}

interface IExtendedAsset extends IAsset {
    isUpload?: boolean;
}

interface IRenderAssetThumbnails {
    assets: (IExtendedAsset | ProcessActivityAsset)[];
    className?: string;
    group?: string;
    hideFilenames?: boolean;
    id?: string;
    onClick?: (asset: IExtendedAsset | ProcessActivityAsset) => void;
    onRemove?: (assetId: string) => void;
    selectedAssetId?: string;
    urlBase?: FilesUrlBase;
}

export function RenderAssetThumbnails(props: IRenderAssetThumbnails) {
    const [showUrl, setShowUrl] = useState<string | null>(null);
    const [isPdf, setIsPdf] = useState<boolean>(false);
    const [selectedAssetId, setSelectedAssetId] = useState<string | undefined>();

    return (
        <>
            <div className={`layout horizontal-wrap asset-thumbnails ${props.className || ''}`}>
                {props.assets
                    .filter((item) => item.id !== undefined && item.filename !== undefined)
                    .map((asset, index) => {
                        let url = '';
                        if ('isUpload' in asset && asset.isUpload) {
                            url = `/api/uploads/${asset.id}`;
                        } else if (props.urlBase && props.id) {
                            url = `/api/${props.urlBase}/${props.id}/assets/${asset.id}`;
                        }

                        const isImageOrPdf = checkIsImageOrPdf(asset.filename);
                        const isSelectedAsset = props.selectedAssetId === asset.id;
                        const isFilePdf = asset.filename.toLowerCase().includes('.pdf');
                        const href = !isImageOrPdf || (isFilePdf && isBrowserInternetExplorer()) ? url : undefined;

                        return (
                            <div key={index}>
                                <div className={classnames('thumbnail', isSelectedAsset ? 'selected' : '')}>
                                    <a
                                        className="thumbnail__link"
                                        href={href}
                                        onClick={() => {
                                            if (href) {
                                                return;
                                            }
                                            if (props.onClick && asset) {
                                                props.onClick(asset);
                                            } else {
                                                setShowUrl(url);
                                                setSelectedAssetId(asset.id);
                                                if (asset.filename.toLowerCase().includes('.pdf')) {
                                                    setIsPdf(true);
                                                } else {
                                                    setIsPdf(false);
                                                }
                                            }
                                        }}
                                        rel="noreferrer"
                                        target="_blank"
                                    >
                                        <img
                                            className="thumbnail__img"
                                            src={url + '?thumb=true'}
                                        />
                                    </a>
                                    {props.onRemove && (
                                        <button
                                            className="btn btn-close thumbnail__close"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                props.onRemove!(asset.id!);
                                            }}
                                        />
                                    )}
                                </div>
                                {!props.hideFilenames && <span>{asset.filename ? asset.filename : ''}</span>}
                            </div>
                        );
                    })}
            </div>

            {showUrl && (
                <ViewerModal
                    assets={props.assets}
                    group={props.group}
                    id={props.id}
                    isPdf={isPdf}
                    onClose={() => setShowUrl(null)}
                    selectedFileId={selectedAssetId}
                    url={showUrl}
                    urlBase={props.urlBase}
                />
            )}
        </>
    );
}

export function RequestAssetThumbnails(props: {
    assets: IAsset[];
    group?: string;
    onRemove?: (id: string) => void;
    requestId: string;
}) {
    return (
        <RenderAssetThumbnails
            assets={props.assets}
            group={props.group}
            id={props.requestId}
            onRemove={props.onRemove}
            urlBase={FilesUrlBase.requests}
        />
    );
}

/**
 * @deprecated use ksyos/design-system components instead. Remove if no longer used.
 */
export function AssetThumbnailsWithHeader(props: {
    assets?: IAsset[];
    header?: string;
    id: string;
    urlBase?: FilesUrlBase;
    withFormDivider?: boolean;
}) {
    if (!props.assets || props.assets.length === 0) {
        return null;
    }

    return (
        <>
            {props.withFormDivider && <FormDivider />}
            <HeaderDivider text={props.header ? props.header : translateKey('procedure.procedure')} />
            <FormGroup>
                <RenderAssetThumbnails
                    assets={props.assets}
                    id={props.id}
                    urlBase={props.urlBase || FilesUrlBase.requests}
                />
            </FormGroup>
        </>
    );
}

export function ProcedureAssetThumbnails(props: {
    assets: IAsset[];
    group?: string;
    onRemove?: (id: string) => void;
    procedureId: string;
}) {
    return (
        <RenderAssetThumbnails
            assets={props.assets}
            group={props.group}
            id={props.procedureId}
            onRemove={props.onRemove}
            urlBase={FilesUrlBase.procedures}
        />
    );
}

export function DiagnosticReportAssetThumbnails(props: {
    assets: IAsset[];
    diagnosticReportId: string;
    group?: string;
    onRemove?: (id: string) => void;
}) {
    return (
        <RenderAssetThumbnails
            assets={props.assets}
            group={props.group}
            id={props.diagnosticReportId}
            onRemove={props.onRemove}
            urlBase={FilesUrlBase.diagnosticReports}
        />
    );
}

/**
 * @deprecated use ksyos/design-system components instead. Remove if no longer used.
 */
export function ClarificationMessage(props: { sender: string | null; text: string }) {
    return (
        <div className="form-group reaction">
            <div className="reaction__author">
                <Icon
                    color={IconColor.inverted}
                    icon="male-care-provider"
                />
                <p>{props.sender}</p>
            </div>
            <div className="reaction__message">
                <UserTextParagraph>{props.text}</UserTextParagraph>
            </div>
        </div>
    );
}
